import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-cyan)] disabled:opacity-50 disabled:pointer-events-none cursor-pointer [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-gradient-to-r from-[var(--accent-cyan)] to-[var(--accent-violet)] text-[var(--bg)] font-semibold shadow-[0_0_24px_rgba(0,240,255,0.35)] hover:shadow-[0_0_32px_rgba(0,240,255,0.55)] hover:brightness-110",
        accent:
          "bg-gradient-to-r from-[var(--accent-magenta)] to-[var(--accent-violet)] text-white font-semibold shadow-[0_0_24px_rgba(255,43,214,0.35)] hover:shadow-[0_0_32px_rgba(255,43,214,0.55)] hover:brightness-110",
        outline:
          "border border-[var(--border-strong)] bg-[rgba(20,24,51,0.4)] text-[var(--ink)] hover:bg-[rgba(20,24,51,0.7)] hover:border-[var(--accent-cyan)] hover:text-[var(--accent-cyan)] backdrop-blur-sm",
        ghost:
          "text-[var(--ink-soft)] hover:bg-[rgba(168,156,255,0.08)] hover:text-[var(--ink)]",
        danger:
          "bg-[rgba(255,56,96,0.15)] border border-[rgba(255,56,96,0.4)] text-[var(--danger)] hover:bg-[rgba(255,56,96,0.25)] hover:shadow-[0_0_20px_rgba(255,56,96,0.3)]",
        success:
          "bg-[rgba(163,255,18,0.12)] border border-[rgba(163,255,18,0.35)] text-[var(--success)] hover:bg-[rgba(163,255,18,0.2)]",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4",
        lg: "h-12 px-6 text-base",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "outline",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
